import React from 'react';
import { render } from 'react-dom';

import Logger from '@cvent/nucleus-logging';
const LOG = new Logger('event-guestside-site/src/main');

import { loadCustomFonts } from 'nucleus-widgets/redux/modules/customFonts';
import locales from '../appInitialization/locales';
import { loadIntlWithLocale } from 'nucleus-text/intlLoaders/index';
import getMatchingLocale from 'nucleus-text/util/getMatchingLocale';
import { setCurrencyCode, setLocale } from 'nucleus-guestside-site/src/redux/modules/text';
import { createApp } from './App';
import {
  createLoadAccountSnapshotAction,
  createLoadEventSnapshotAction,
  loadLanguageManagementSettings
} from '../redux/actions';
import { transformAndLoadEventArchivedPageData } from './actions';
import reducer from './reducer';

import { Provider } from 'react-redux';
import DialogContainer from 'nucleus-guestside-site/src/containers/DialogContainer';
import { loadEventTimezoneIntoStore } from '../redux/timezones';

// Get the event widget factory.
import WidgetFactory from '../widgetFactory';
import EventEmailClient from '../clients/EventEmailClient';

async function startArchivedEventAppRender(appSettings, preloadedData) {
  render(
    <div />,
    document.getElementById('react-mount')
  );
  // using require instead of import for conditional compilation;
  let createStoreWithMiddleware;
  const baseUrl = `${appSettings.serviceBaseUrl}/v1/`;
  if (process.env.NODE_ENV === 'production') {
    createStoreWithMiddleware = require('nucleus-guestside-site/src/redux/prodCreateStoreWithMiddleware').default;
  } else {
    createStoreWithMiddleware = require('nucleus-guestside-site/src/redux/devCreateStoreWithMiddleware').default;
  }
  const store = createStoreWithMiddleware(reducer, {
    account: { name: appSettings.accountName },
    environment: appSettings.environment,
    userSession: { eventId: appSettings.eventId },
    widgetFactory: new WidgetFactory(),
    clients: {
      eventEmailClient: new EventEmailClient(baseUrl, appSettings.eventId, appSettings.environment)
    }
  });
  store.dispatch(createLoadAccountSnapshotAction(preloadedData.assets.accountSnapshot));
  store.dispatch(await createLoadEventSnapshotAction(preloadedData.assets.eventSnapshot, store.getState().account));
  store.dispatch(transformAndLoadEventArchivedPageData(preloadedData.assets.eventArchivePageData));

  const { text, event, account: { customFonts = [] } } = store.getState();
  store.dispatch(setLocale(appSettings.cultureCode));
  store.dispatch(loadEventTimezoneIntoStore(appSettings.eventTimezoneId, preloadedData.assets.eventTimezone.timeZones,
    preloadedData.assets.eventTimezone.translations));
  const activeCustomFonts = customFonts.filter(font => font.isActive);
  if (activeCustomFonts.length > 0) {
    store.dispatch(loadCustomFonts(activeCustomFonts));
  }
  // Register Translations
  const translationsPromise = new Promise(getMatchingLocale(event.cultureCode, locales));
  await new Promise(resolve => loadIntlWithLocale(event.cultureCode, resolve));
  text.resolver.registerTranslations(await translationsPromise, 'default', event.cultureCode);
  store.dispatch(setCurrencyCode(event.eventCurrencySnapshot.isoAlphabeticCode));
  store.dispatch(loadLanguageManagementSettings());

  const App = createApp(appSettings.assetRoot || '/');

  render(
    <Provider store={store}>
      <div>
        <App />
        <DialogContainer
          spinnerMessage="EventGuestSide_SharedLoadingProgressWidget_SpinnerLoadingText__resx"
          message="EventGuestSide_SharedLoadingProgressWidget_MessageText__resx"
        />
      </div>
    </Provider>, document.getElementById('react-mount'));

  // remove devtools if browser plugin https://github.com/zalmoxisus/redux-devtools-extension present.
  if (process.env.NODE_ENV !== 'production') {
    if (window.devToolsExtension) {
      LOG.info('using browser extension redux dev tools instead of normal redux dev tools');
    } else {
      const target = document.getElementById('react-mount');
      const devTarget = document.createElement('div');
      const DevTools = require('nucleus-guestside-site/src/redux/DevTools').default;
      const devToolsElement = (
        <Provider store={store}>
          <DevTools />
        </Provider>
      );
      if (target && target.parentNode) {
        target.parentNode.insertBefore(devTarget, target.nextSibling);
      } else {
        LOG.error('failed to insert devtools into DOM');
      }
      render(devToolsElement, devTarget);
    }
  }

  runTriggerHandlers(appSettings.eventContext);
}

function runTriggerHandlers(eventContext) {
  const isPlanner = eventContext.isPlanner;
  const isPreview = eventContext.isPreview;
  const isTestMode = eventContext.isTestMode;
  if (isTestMode || (!isPlanner && !isPreview)) {
    if (window.CVENT && window.CVENT.runTriggerHandlers) {
      window.CVENT.runTriggerHandlers('AllPages');
    }
  }
}


export default startArchivedEventAppRender;
